/**
 * Support Coupon Strategy
 * Handles SUPPORT type coupons for customer support scenarios
 * Special coupon type that applies to all discount rate plans
 */
import { BaseCouponStrategy } from './base-coupon-strategy';
import { couponTypes, couponSourceTypeMap } from '../helper/constant';
import { getDefaultCouponState, processCoupon } from '../helper/utils';
import { flowTypes, pageConfig } from '@/common/service/page-config';
import { logger } from '@/utils/log';

export class SupportCouponStrategy extends BaseCouponStrategy {
  /**
   * Get coupon type identifier
   * @returns {string} TIMEBASE coupon type (support uses timebase calculation)
   */
  getType() {
    return couponTypes.TIMEBASE;
  }

  /**
   * Get source type identifier
   * @returns {string} SUPPORT source type
   */
  getSourceType() {
    return couponSourceTypeMap.supportCoupon;
  }

  /**
   * Calculate discount for SUPPORT coupon
   * Support coupons use similar calculation to timebase
   * @param {Object} couponState - Current coupon state
   * @param {Array} selectedPlans - Selected plans array
   * @param {Object} planWithAmountMap - Plan amount mapping
   * @param {Object} context - Additional context
   * @returns {Object} Calculated discount result
   */
  // eslint-disable-next-line no-unused-vars
  calculateDiscount(couponState, selectedPlans, planWithAmountMap, context) {
    const discountPlanList = this.transformSupportToDiscountList(couponState);

    if (discountPlanList.length === 0) {
      return this.getInitialResult();
    }

    return processCoupon({
      discountPlanList,
      selectedPlanList: selectedPlans,
      planWithAmountMap,
    });
  }

  /**
   * Transform support coupon data to discount plan list
   * @param {Object} couponState - Coupon state with timeBase array
   * @returns {Array} List of discount plans
   */
  transformSupportToDiscountList(couponState) {
    const timeBase = couponState?.timeBase || [];

    return timeBase
      .filter((item) => item.plan_code)
      .map((item) => ({
        planCode: item.plan_code,
        discount: item.discount,
        discountType: item.discountType,
        expireDate: item.expireDate,
        couponDefType: item.couponDefType,
        couponPeriod: item.couponPeriod,
      }));
  }

  /**
   * Convert API result to support coupon state
   * @param {Object} apiResult - API response
   * @param {Object} options - Conversion options
   * @returns {Object} Coupon state
   */
  convertApiResultToState(apiResult, options = {}) {
    const { couponCode = '', allowChange = false } = options;
    const defaultState = getDefaultCouponState();

    // Transform API result to timeBase format (similar to timebase)
    const timeBase = (apiResult || [])
      .filter((item) => item.planCode)
      .map((v) => ({
        plan_code: v.planCode,
        discount: v.couponDiscount,
        discountType: v.couponDiscountType,
        expireDate: v.couponExpireDate,
        couponDefType: v.couponDefType,
        couponPeriod: v.couponPeriod,
      }));

    return {
      ...defaultState,
      couponType: couponTypes.TIMEBASE,
      couponSourceType: couponSourceTypeMap.supportCoupon,
      timeBase,
      couponCode,
      allowChange, // Support coupons typically cannot be changed
    };
  }

  /**
   * Validate support coupon code
   * @param {string} couponCode - Coupon code to validate
   * @param {Object} context - Validation context
   * @returns {Promise<Object>} Validation result
   */
  // eslint-disable-next-line no-unused-vars
  async validate(couponCode, context) {
    return {
      valid: true,
      couponCode,
    };
  }

  /**
   * Check if support coupon can be applied
   * Support coupons have specific eligibility rules
   * @param {Object} context - Application context
   * @returns {boolean} Whether coupon can be applied
   */
  // eslint-disable-next-line no-unused-vars
  canApply(context) {
    // Support coupons only work in edit flow
    if (![flowTypes.edit].includes(pageConfig?.type)) {
      logger.info('Support coupon only for edit flow');
      return false;
    }

    return true;
  }

  /**
   * Check if current flow is eligible for support coupon
   * @param {Object} context - Context with flow and campaign info
   * @returns {Object} Eligibility result
   */
  checkEligibility(context) {
    const { isFromMarketingCampaign = false, flowType } = context;

    // Not eligible if from marketing campaign
    if (isFromMarketingCampaign) {
      return {
        eligible: false,
        reason: 'Support coupon ineligible for campaign cases',
      };
    }

    // Only eligible for edit flow
    if (flowType !== flowTypes.edit) {
      return {
        eligible: false,
        reason: 'Support coupon only for edit flow',
      };
    }

    return {
      eligible: true,
      reason: null,
    };
  }
}

// Export singleton instance
export const supportCouponStrategy = new SupportCouponStrategy();
